import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Box, Checkbox, Grid, TextField, Typography } from '@mui/material';
import { formStyle, stepNumberStyle, formTitleStyle, formDesStyle, underlineStyle, topUnderlineStyle, sectionContainer, smallLabel, defaultLabel, titleLabel } from '../../../constant/material_style';
import CustomTextfield from '../../../component/custom_textfield';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { guest_checkin_type, guest_checkout_type, staff_checkin_type, staff_checkout_type } from '../../../constant/common_use';
import { useStateContext } from '../../../contexts/StepFormContextProvider';
import { handleDeleteAttachment, handleUpdateForm, uploadImageToServer } from '../../../api/rc_admission_form';
import { b64toBlob, base64ToImage, uploadImgToServer } from '../../../utils/helper/rc_form_helper';

const ServiceAgreementSchedule3 = ({ isAdmin, currentStep, setCurrentStep, formValues, setFormValues, getSignature, handleUpdateSignature }) => {
	const { control, handleSubmit, setValue, formState: { errors }} = useForm({ mode: 'onChange'});
	const { formId } = useStateContext();

	const [savedSignature, setSavedSignature] = useState({
		staff_checkin_type: false,
		guest_checkin_type: false,
		staff_checkout_type: false,
		guest_checkout_type: false
	});

	useEffect(() => {
		setValue('staff_checkin_name', getSignature(staff_checkin_type)?.name);
		setValue('staff_checkin_position', getSignature(staff_checkin_type)?.position);
		setValue('staff_checkout_name', getSignature(staff_checkout_type)?.name);
		setValue('staff_checkout_position', getSignature(staff_checkout_type)?.position);
	}, [setValue, formValues]);

	useEffect(() => {
		if(!isAdmin){
			staffCheckinRef.current.clear();
			staffCheckoutRef.current.clear();
			staffCheckinRef.current.off();
			staffCheckoutRef.current.off();
		}
	}, [isAdmin]);

	const [newInventoryTitle, setNewInventoryTitle] = useState('');
	const staffCheckinRef = useRef({});
	const staffCheckoutRef = useRef({});
	const guestCheckinRef = useRef({});
	const guestCheckoutRef = useRef({});

	const debounceSetInventoryValues = useCallback(
		_.debounce((name, value) => {
			setValue(name, value, { shouldValidate: true });

			setFormValues((prev) => {
				const newFormValues = { ...prev, [name]: value };
				const updatedValue = { [name]: value };

				const newFieldData = updatedValue.items.find(section => section.title === "Others").inventories.slice(-1)[0];
				const convertedNewData = {
					items: [
						{
							name: newFieldData.title,
							check_in: newFieldData.isCheckIn,
							check_out: newFieldData.isCheckOut
						}
					]
				}

				if(newFieldData) {
					handleUpdateForm(formId, convertedNewData);
				}

				setFormValues(newFormValues);
				return newFormValues;
			});
		}, 500), [setValue, setFormValues, formId]
	);

	const debounceSetValues = useCallback(
		_.debounce((name, value, type) => {
			setValue(name, value, { shouldValidate: true });

			setFormValues((prev) => {
				const newFormValues = { ...prev, [name]: value }
				const updatedValue = value.filter(val => val.type === type);

				if(formId) {
					handleUpdateForm(formId, {signatures: updatedValue});
				}

				setFormValues(newFormValues);
				return newFormValues;
			});
		}, 800), [setValue, setFormValues, formId]
	);

    const handleCheckboxChange = (sectionIndex, inventoryIndex, key) => async (event) => {
        const updatedInventories = [...formValues.items];
        updatedInventories[sectionIndex].inventories[inventoryIndex][key] = event.target.checked ? 1 : 0;

		const inventory = updatedInventories[sectionIndex].inventories[inventoryIndex];

		const structuredInventory = {
			name: inventory.title,
			check_in: inventory.isCheckIn,
			check_out: inventory.isCheckOut,
			...(inventory.id && { id: inventory.id }),
		};

		const convertedData = [structuredInventory];
		const updatedFormValues = {...formValues, updatedInventories};
		const selectedItem = { items: convertedData };

        setFormValues(updatedFormValues);
		await handleUpdateForm(formId, selectedItem);
    };

	const clearSignature = async (e, signatureRef, type) => {
		e.preventDefault();
		signatureRef.current.clear();

		const filteredData = formValues.signatures.filter((signature) => signature.type === type);

		if (filteredData) {
			const updatedSignatures = formValues.signatures.map((signature) => {
				if(signature.type === type) {
					if(signature.hasOwnProperty('id')) {
						delete signature.id;
					}

					signature.signature_url_s3 = '';
				}

				return signature;
			});

			if (filteredData[0].signature_url_s3.trim() !== '') {
				const deleteSig = {...filteredData[0]};
				delete deleteSig.ic_passport;
				delete deleteSig.name;
				delete deleteSig.type;
				deleteSig.delete = 1;

				await handleDeleteAttachment(formId, { signatures: [deleteSig] });
			}

			setFormValues({ ...formValues, signatures: updatedSignatures });
			await handleUpdateForm(formId, { signatures: filteredData });
		}
	};

	const saveSignature = async (e, canva, type, name, position, date) => {
		e.preventDefault();
		const url = canva.current.getTrimmedCanvas().toDataURL('image/png');
		const base64Data = url.replace(/^data:image\/\w+;base64,/, '');

		const imgFile = base64ToImage(url, type);
		const imgBlob = b64toBlob(base64Data, 'image/png');

		const signatureAttachments = [];
		let imgObj = {
			prefix: "1/admission/documents",
			filename: imgFile.name,
		};

		signatureAttachments.push(imgObj);
		let transformData = { payload: signatureAttachments }
		const res = await uploadImageToServer(transformData);

		const convertedAttachments = [];
		if(res) {
			const uploadUrl = Object.keys(res).map(key => {
				const attachmentArr = res[key];
				return attachmentArr[0].uploadUrl;
			}).pop();

			await uploadImgToServer(uploadUrl, imgBlob);

			Object.keys(res).forEach(key => {
				const attachmentArr = res[key];

				attachmentArr.forEach(attachment => {
					const convertedFiles = {
						attachment_url_s3: attachment?.downloadUrl,
						attachment_name: attachment?.filePath.split('/').pop(),
					}

					convertedAttachments.push(convertedFiles);
				});
			})
		}

		const result = handleUpdateSignature(type, 'signature_url_s3', convertedAttachments[0].attachment_url_s3);
		if(result) {
			setSavedSignature(prevState => ({
				...prevState,
				[type]: true
			}));
		}
	};

    const handleTextFieldChange = (e) => {
		setNewInventoryTitle(e.target.value);
    };

	const handleAddInventory = (event) => {
		event.preventDefault();
		if (newInventoryTitle.trim() !== '') {
		  	const newInventory = {
				title: newInventoryTitle.trim(),
				isCheckIn: 0,
				isCheckOut: 0
			};

			const updatedFormValues = { ...formValues };
			const fifthSectionIndex = updatedFormValues.items.findIndex(section => section.title === "Others");

			if (fifthSectionIndex !== -1) {
				updatedFormValues.items[fifthSectionIndex].inventories.push(newInventory);
			} else {
				updatedFormValues.inventories.push({
					title: "Others",
					inventories: [newInventory]
				});
			}

			// Debounce the state update
			debounceSetInventoryValues('items', updatedFormValues.items);
			setNewInventoryTitle('');
		}
	};

	const handleChange = (type, objKey, val) => {
		const updatedSignatures = formValues.signatures.map((signature) => {
			return signature.type === type ? {
				...signature, [objKey]: val,
			} : signature
		});

		debounceSetValues('signatures', updatedSignatures, type);
	}

	const goback = (e) => {
		setCurrentStep(currentStep - 1);
	}

	const onSubmit = (e) => {
		setCurrentStep(currentStep + 1);
	}

	const handleSetCurrentDateTime = () => {
		const currentDateTime = new Date();

		const year = currentDateTime.getFullYear();
		const month = String(currentDateTime.getMonth() + 1).padStart(2, '0');
		const day = String(currentDateTime.getDate()).padStart(2, '0');

		const hours = String(currentDateTime.getHours()).padStart(2, '0');
		const minutes = String(currentDateTime.getMinutes()).padStart(2, '0');

		const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

		return formattedDateTime;
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Box style={formStyle}>
				<Grid container alignItems="flex-start" spacing={2} style={{ marginBottom: '20px', overflowY: 'auto' }}>
					<Grid item>
						<Box style={stepNumberStyle}>{currentStep}</Box>
					</Grid>
					<Grid item xs style={{ paddingLeft: '0' }}>
						<Typography style={formTitleStyle}>Service Agreement - Schedule 3</Typography>
						<Typography style={formDesStyle}>Provider's Fixtures and Fittings</Typography>
					</Grid>
				</Grid>

				{/* per section */}
				<Box style={sectionContainer}>
					<Box style={topUnderlineStyle}>
						<Typography style={formDesStyle}>Schedule 3 (List of Inventory)</Typography>
					</Box>
				</Box>

				<Box style={sectionContainer}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Grid container alignItems="center" spacing={1} style={{ marginBottom: '14px' }}>
								<Grid item xs={8} sm={8} />
								<Grid item xs={4} sm={4} style={{ display: 'flex', justifyContent: 'space-around' }}>
									<Typography style={smallLabel}>Checkin</Typography>
									<Typography style={smallLabel}>Checkout</Typography>
								</Grid>
							</Grid>
							{formValues.items?.map((item, mainIndex) => (
								<React.Fragment key={mainIndex}>
									{item.inventories.map((inventory, index) => (
										<Grid container alignItems="center" spacing={1} key={index} className='inventory-list'>
											<Grid item xs={8} sm={8}>
												<Typography style={smallLabel}>{inventory.title}</Typography>
											</Grid>
											<Grid item xs={4} sm={4} style={{ display: 'flex', justifyContent: 'space-around', paddingTop: '0px', paddingBottom: '0px' }}>
												<Checkbox
													onKeyDown={(event) => {
														if (event.key === 'Enter') {
															event.preventDefault();
														}
													}}
													checked={Boolean(inventory.isCheckIn)}
													onChange={handleCheckboxChange(mainIndex, index, 'isCheckIn')}
													sx={{
														'& .MuiSvgIcon-root': {
															fontSize: 28,
															backgroundColor: '#D9D9D9',
														},
														'&.Mui-checked .MuiSvgIcon-root': {
															borderRadius: '4px',
															color: 'white',
															backgroundColor: '#5A46AA'
														},
													}}
												/>
												<Checkbox
													onKeyDown={(event) => {
														if (event.key === 'Enter') {
															event.preventDefault();
														}
													}}
													checked={Boolean(inventory.isCheckOut)}
													onChange={handleCheckboxChange(mainIndex, index, 'isCheckOut')}
													sx={{
														'& .MuiSvgIcon-root': {
															fontSize: 28,
															backgroundColor: '#D9D9D9',
														},
														'&.Mui-checked .MuiSvgIcon-root': {
															borderRadius: '4px',
															color: 'white',
															backgroundColor: '#5A46AA'
														}
													}}
												/>
											</Grid>
										</Grid>
									))}
									{ mainIndex < 4 ? (
										<Box style={sectionContainer}>
											<Box style={underlineStyle} />
										</Box>
									) : null }
								</React.Fragment>
							))}
						</Grid>
					</Grid>
				</Box>

				{
					isAdmin &&
					<Box>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Grid container alignItems="center" spacing={1} style={{ marginBottom: '14px', paddingTop: '0px' }}>
									<Grid item xs={6} sm={4}>
										<TextField
											variant="outlined"
											fullWidth
											value={newInventoryTitle}
											onChange={handleTextFieldChange}
											onKeyDown={(event) => {
												if (event.key === 'Enter') {
													handleAddInventory(event);
												}
											}}
											sx={{
												'& .MuiOutlinedInput-root input': {
													padding: '8px 15px',
												},
												'& .MuiOutlinedInput-root': {
													marginBottom: '10px',
													borderRadius: '30px',
													marginLeft: '0px',
													fontSize: '12px',
												},
											}}
											disabled={!isAdmin}
											placeholder={isAdmin? "Staff Input" : ""}
										/>
									</Grid>
									<Grid item xs={2} sm={4}></Grid>
									<Grid item xs={4} sm={4} style={{ display: 'flex', justifyContent: 'space-around', paddingTop: '0px', paddingBottom: '0px' }}>
										<Checkbox
											disabled
											sx={{
												'& .MuiSvgIcon-root': {
													fontSize: 28,
													backgroundColor: '#D9D9D9',
												},
												'&.Mui-checked .MuiSvgIcon-root': {
													borderRadius: '4px',
													color: 'white',
													backgroundColor: '#5A46AA'
												},
											}}
										/>
										<Checkbox
											disabled
											sx={{
												'& .MuiSvgIcon-root': {
													fontSize: 28,
													backgroundColor: '#D9D9D9',
												},
												'&.Mui-checked .MuiSvgIcon-root': {
													borderRadius: '4px',
													color: 'white',
													backgroundColor: '#5A46AA'
												}
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Box>
				}

				<Box style={sectionContainer}>
					<Typography style={titleLabel}>Checkin</Typography>
					<Typography style={defaultLabel}>
						I hereby confirm that the information checked in this form is true, complete and accurate.
					</Typography>
                </Box>
				<Box style={sectionContainer}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<div className='datetime-container'>
								<CustomTextfield name="checkin_date" formValue={getSignature(staff_checkin_type)?.date} label="Date & Time" typeOfField="datetimePicker" onChangeVal={(val) => handleChange(staff_checkin_type, 'date', val)} disableFuture={false} disablePast={false} readOnly={!isAdmin} isAdmin={isAdmin} />
								<button
									className="t-accept-button" type='button'
									onClick={() => handleChange(staff_checkin_type, 'date', handleSetCurrentDateTime())} disabled={!isAdmin}
								>
									Now
								</button>
							</div>
							<CustomTextfield name='staff_checkin_name' formValue={getSignature(staff_checkin_type)?.name} label="Full Name" typeOfField="text1" onChangeVal={(name, val) => handleChange(staff_checkin_type, 'name', val)} control={control} errors={errors}  readOnly={!isAdmin} isAdmin={isAdmin} />
							<CustomTextfield name='staff_checkin_position' formValue={getSignature(staff_checkin_type)?.position} label="Position" typeOfField="text1" onChangeVal={(name, val) => handleChange(staff_checkin_type, 'position', val)} control={control} errors={errors}  readOnly={!isAdmin} isAdmin={isAdmin} />
						</Grid>
						<Grid item xs={12}>
							<CustomTextfield
								name={getSignature(staff_checkin_type)} formValue={getSignature(staff_checkin_type)?.signature_url_s3} label="Komune Care Staff Signature" typeOfField="signature" sigCanvas={staffCheckinRef}
								saveSignature={(e) => saveSignature(e, staffCheckinRef, staff_checkin_type, getSignature(staff_checkin_type)?.name, getSignature(staff_checkin_type)?.position, getSignature(staff_checkin_type)?.date)}
								clearSignature={(e) => clearSignature(e, staffCheckinRef, staff_checkin_type)} savedSignature={savedSignature.staff_checkin_type} readOnly={!isAdmin} isAdmin={isAdmin}
							/>
						</Grid>
						<Grid item xs={12}>
							<CustomTextfield
								name={getSignature(guest_checkin_type)} formValue={getSignature(guest_checkin_type)?.signature_url_s3} label="Guest Signature" typeOfField="signature" sigCanvas={guestCheckinRef}
								saveSignature={(e) => saveSignature(e, guestCheckinRef, guest_checkin_type)} savedSignature={savedSignature.guest_checkin_type}
								clearSignature={(e) => clearSignature(e, guestCheckinRef, guest_checkin_type)}
							/>
						</Grid>
					</Grid>
                </Box>

				<Box style={sectionContainer}>
					<Typography style={titleLabel}>Checkout</Typography>
					<Typography style={defaultLabel}>
						I hereby confirm that the information checked in this form is true, complete and accurate.
					</Typography>
                </Box>
				<Box style={sectionContainer}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<div className='datetime-container'>
								<CustomTextfield name="checkout_date" formValue={getSignature(staff_checkout_type)?.date} label="Date & Time" typeOfField="datetimePicker" onChangeVal={(val) => handleChange(staff_checkout_type, 'date', val)} disableFuture={false} disablePast={false} readOnly={!isAdmin} isAdmin={isAdmin} />
								<button
									className="t-accept-button" type='button'
									onClick={() => handleChange(staff_checkout_type, 'date', handleSetCurrentDateTime())} disabled={!isAdmin}
								>
									Now
								</button>
							</div>
							<CustomTextfield name="staff_checkout_name" formValue={getSignature(staff_checkout_type)?.name} label="Full Name" typeOfField="text1" onChangeVal={(name, val) => handleChange(staff_checkout_type, 'name', val)} control={control} errors={errors} readOnly={!isAdmin} isAdmin={isAdmin} />
							<CustomTextfield name="staff_checkout_position" formValue={getSignature(staff_checkout_type)?.position} label="Position" typeOfField="text1" onChangeVal={(name, val) => handleChange(staff_checkout_type, 'position', val)} control={control} errors={errors} readOnly={!isAdmin} isAdmin={isAdmin} />
						</Grid>
						<Grid item xs={12}>
							<CustomTextfield
								name={getSignature(staff_checkout_type)} formValue={getSignature(staff_checkout_type)?.signature_url_s3} label="Komune Care Staff Signature" typeOfField="signature" sigCanvas={staffCheckoutRef}
								saveSignature={(e) => saveSignature(e, staffCheckoutRef, staff_checkout_type, getSignature(staff_checkout_type)?.name, getSignature(staff_checkout_type)?.position)} savedSignature={savedSignature.staff_checkout_type}
								clearSignature={(e) => clearSignature(e, staffCheckoutRef, staff_checkout_type)} readOnly={!isAdmin}
							/>
						</Grid>
						<Grid item xs={12}>
							<CustomTextfield
								name={getSignature(guest_checkout_type)} formValue={getSignature(guest_checkout_type)?.signature_url_s3} label="Guest Signature" typeOfField="signature" sigCanvas={guestCheckoutRef}
								saveSignature={(e) => saveSignature(e, guestCheckoutRef, guest_checkout_type)} savedSignature={savedSignature.guest_checkout_type}
								clearSignature={(e) => clearSignature(e, guestCheckoutRef, guest_checkout_type)}
							/>
						</Grid>
					</Grid>
                </Box>

				<Box style={sectionContainer}>
					<div style={{ marginTop: '40px' }}>
						<Typography style={smallLabel}>
							**I/We (the tenant(s)) understand that unless otherwise noted, all discrepancies will be the guest's responsibility and will be deducted from the security deposit at the time of move-out.
						</Typography>
					</div>
				</Box>

				<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
					<button className='t-accept-button' onClick={goback}>Previous</button>
					<button type="submit" className='t-accept-button'>Next</button>
				</div>
			</Box>
		</form>
	);
}

export default ServiceAgreementSchedule3